'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { keyframes } from 'styled-components';

interface PreferencesToggleProps {
  fontSize?: string;
  active: boolean;
  onClick(checked: boolean): void;
}

export const PreferencesToggleNew: React.FC<PreferencesToggleProps> = props => {
  const prevActiveRef = React.useRef(props.active);
  const shouldAnimate = prevActiveRef.current !== props.active;

  React.useLayoutEffect(() => {
    prevActiveRef.current = props.active;
  }, [props.active]);

  const handleClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      props.onClick(!props.active);
    },
    [props],
  );

  return (
    <SwitchWrapper fontSize={props.fontSize as string} onClick={handleClick}>
      <KnobOuterCircle $animate={shouldAnimate} $check={props.active} />
      <OnLabel $animate={shouldAnimate} $check={props.active}>
        ON
      </OnLabel>
      <OffLabel $animate={shouldAnimate} $check={props.active}>
        OFF
      </OffLabel>
    </SwitchWrapper>
  );
};

const SwitchWrapper = styled.div<{ fontSize: string }>`
  display: flex;
  font-size: ${props => (props.fontSize ? props.fontSize : '10px')};
  font-weight: 500;
  border-radius: 1.5em;
  background-color: ${props => props.theme.colors.backgroundInactive};
  width: 64px;
  height: 30px;
  align-items: center;
  padding: 0.5em;
  position: relative;
  cursor: pointer;
`;

const goLeft = keyframes`
  from {
    top: 0.5em;
    left: 3.8em;
  }
  to {
    top: 0.5em;
    left: 0.5em;
  }
`;

const goRight = keyframes`
  from {
    top: 0.5em;
    left: 0.5em;
  }
  to {
    top: 0.5em;
    left: 3.8em;
  }
`;

const appear = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const disappear = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const KnobOuterCircle = styled.div<{ $animate: boolean; $check: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2em;
  width: 2em;
  border-radius: 1em;
  background-color: ${props => (props.$check ? props.theme.colors.brand : '#929EAE')};
  z-index: 1;
  position: absolute;
  top: 0.5em;
  left: ${props => (props.$check ? '3.8em' : '0.5em')};
  animation: ${props => (props.$animate ? (props.$check ? goRight : goLeft) : 'none')} 0.15s linear forwards;
`;

const OnLabel = styled.div<{ $animate: boolean; $check: boolean }>`
  animation: ${props => (props.$animate ? (props.$check ? appear : disappear) : 'none')} 0.15s linear forwards;
  opacity: ${props => (props.$check ? 1 : 0)};
  position: absolute;
  left: 1.75em;
  top: 1.55em;
  transform: translate(-50%, -50%);
  color: ${props => props.theme.colors.foregroundActive};
`;

const OffLabel = styled.div<{ $animate: boolean; $check: boolean }>`
  animation: ${props => (props.$animate ? (props.$check ? disappear : appear) : 'none')} 0.15s linear forwards;
  opacity: ${props => (props.$check ? 0 : 1)};
  position: absolute;
  left: 4.75em;
  top: 1.55em;
  transform: translate(-50%, -50%);
  color: ${props => props.theme.colors.foregroundActive};
`;
